import { useState } from 'react';
import { comparisonData, testimonials } from '@/mocks/categories';
import { Link } from 'react-router-dom';

export default function WhyChooseSection() {
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const current = testimonials[testimonialIndex];

  const prevTestimonial = () => {
    setTestimonialIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };
  const nextTestimonial = () => {
    setTestimonialIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  return (
    <section className="py-16 md:py-24 bg-background-100 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <p className="text-xs font-semibold text-primary-600 uppercase tracking-wider mb-3">Why OOPBuy</p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4">
            Why Thousands of Users Choose OOPBuy Spreadsheet
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            Discover why smart shoppers worldwide have switched to the spreadsheet-based shopping method.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-16">
          <div className="p-5 md:p-6 bg-background-50 rounded-xl border border-background-200/70">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mb-4">
              <i className="ri-money-dollar-circle-line text-accent-600 text-lg"></i>
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Lower Prices</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Access products at prices significantly lower than retail platforms through direct sourcing and spreadsheet comparison.
            </p>
          </div>
          <div className="p-5 md:p-6 bg-background-50 rounded-xl border border-background-200/70">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mb-4">
              <i className="ri-eye-off-line text-accent-600 text-lg"></i>
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Hidden Gems</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Discover supplier-exclusive products that are not available on mainstream shopping platforms.
            </p>
          </div>
          <div className="p-5 md:p-6 bg-background-50 rounded-xl border border-background-200/70">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mb-4">
              <i className="ri-speed-up-line text-accent-600 text-lg"></i>
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Faster Decisions</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              The spreadsheet format enables quick sorting, filtering, and side-by-side comparison for instant purchase decisions.
            </p>
          </div>
          <div className="p-5 md:p-6 bg-background-50 rounded-xl border border-background-200/70">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mb-4">
              <i className="ri-scales-3-line text-accent-600 text-lg"></i>
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Easy Comparison</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Compare prices, ratings, and availability across hundreds of products in a single view.
            </p>
          </div>
          <div className="p-5 md:p-6 bg-background-50 rounded-xl border border-background-200/70">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mb-4">
              <i className="ri-fire-line text-accent-600 text-lg"></i>
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Trending Weekly</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Stay ahead with weekly updated trending products and emerging fashion finds added to the spreadsheet.
            </p>
          </div>
          <div className="p-5 md:p-6 bg-background-50 rounded-xl border border-background-200/70">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mb-4">
              <i className="ri-ship-line text-accent-600 text-lg"></i>
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Shipping Insights</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Built-in shipping estimates and logistics information help you plan purchases more efficiently.
            </p>
          </div>
        </div>

        <div className="mb-16">
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
            OOPBuy vs Other Shopping Methods
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-background-300">
                  <th className="py-4 px-4 font-semibold text-foreground-800">Feature</th>
                  <th className="py-4 px-4 font-semibold text-primary-600">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-file-list-3-line"></i> OOPBuy Spreadsheet
                    </span>
                  </th>
                  <th className="py-4 px-4 font-semibold text-foreground-800">Traditional Shopping</th>
                  <th className="py-4 px-4 font-semibold text-foreground-800">Other Agents</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, idx) => (
                  <tr key={row.feature} className={`border-b border-background-200/70 ${idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}`}>
                    <td className="py-3.5 px-4 font-medium text-foreground-700">{row.feature}</td>
                    <td className="py-3.5 px-4">
                      <span className="inline-flex items-center gap-1.5 text-primary-600 font-semibold">
                        <i className="ri-check-line text-xs"></i> {row.oopbuy}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-foreground-600">{row.traditional}</td>
                    <td className="py-3.5 px-4 text-foreground-600">{row.agents}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-center mt-6">
            <Link
              to="/oopbuy-spreadsheet-guide"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors"
            >
              Compare More Platforms
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="bg-background-50 rounded-2xl p-6 md:p-10 border border-background-200/70">
          <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-12">
            <div className="lg:w-1/3 flex-shrink-0">
              <div className="w-full aspect-square rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20stylish%20young%20adult%20in%20a%20minimalist%20studio%20setting%20with%20soft%20warm%20lighting%2C%20clean%20cream%20background%2C%20editorial%20fashion%20portrait%2C%20natural%20expression%2C%20warm%20skin%20tones%2C%20commercial%20photography%20style%2C%20authentic%20mood&width=600&height=600&seq=testimonial-person-v3&orientation=squarish"
                  alt="Verified OOPBuy user"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:w-2/3">
              <p className="text-xs font-semibold text-primary-600 uppercase tracking-wider mb-3">User Review</p>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                Trusted by Thousands
                <br />
                <span className="text-foreground-600 font-normal">of Smart Shoppers</span>
              </h3>
              <blockquote className="mb-6">
                <p className="text-base md:text-lg text-foreground-700 leading-relaxed italic">
                  &ldquo;{current.quote}&rdquo;
                </p>
              </blockquote>
              <p className="text-sm font-semibold text-foreground-950 mb-6">
                &mdash; {current.author}, {current.location}
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={prevTestimonial}
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-foreground-950 hover:border-foreground-400 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                </button>
                <button
                  onClick={nextTestimonial}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-line"></i>
                </button>
                <span className="text-xs text-foreground-500 ml-2">
                  {testimonialIndex + 1} / {testimonials.length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}